import React, { useState } from 'react';
import { Network, Code2, Cloud, ShieldCheck, BarChart3, Cpu, Headphones, Map } from 'lucide-react';

const services = [
  {
    num: '01', Icon: Network,
    title: 'Network Infrastructure & Connectivity',
    desc: 'Design, deployment, and management of robust network systems across single or multi-site operations — including fibre, LAN/WAN, PoE+, and industrial-grade wireless connectivity.',
    tags: ['LAN/WAN', 'Fibre', 'Wi-Fi', 'PoE+', 'Multi-site'],
  },
  {
    num: '02', Icon: Code2,
    title: 'Custom Software Architecture',
    desc: 'Bespoke software systems tailored to your business processes — from inventory and procurement platforms to client portals, ERP integrations, and field service management tools.',
    tags: ['ERP', 'Portals', 'APIs', 'Dashboards', 'Mobile'],
  },
  {
    num: '03', Icon: Cloud,
    title: 'Cloud Computing & Remote Access',
    desc: 'Migration, management, and optimisation of cloud environments using Microsoft Azure — enabling remote monitoring, real-time data access, and scalable infrastructure for distributed operations.',
    tags: ['Azure', 'Cloud Migration', 'Remote Access', 'SaaS'],
  },
  {
    num: '04', Icon: ShieldCheck,
    title: 'Cybersecurity & Data Protection',
    desc: 'Comprehensive security audits, firewall configuration, endpoint protection, and data governance frameworks — keeping your business and client data protected at all times.',
    tags: ['Firewall', 'Audit', 'Compliance', 'Endpoint Security'],
  },
  {
    num: '05', Icon: BarChart3,
    title: 'Business Intelligence & Analytics',
    desc: 'Real-time management dashboards and BI platforms giving leadership live visibility into operations, inventory, sales, and performance — turning raw data into clear decisions.',
    tags: ['BI Dashboards', 'Reporting', 'KPIs', 'Analytics'],
  },
  {
    num: '06', Icon: Cpu,
    title: 'IoT & Industrial Connectivity',
    desc: 'Integration of IoT sensors, automation systems, and industrial connectivity platforms — bridging operational technology (OT) and information technology (IT) for smarter industrial operations.',
    tags: ['IoT', 'Automation', 'OT/IT Bridge', 'Sensors', 'LoRa'],
  },
  {
    num: '07', Icon: Headphones,
    title: 'IT Support & Managed Services',
    desc: 'Ongoing IT support, helpdesk services, system maintenance, and infrastructure management — your team focuses on operations while we keep your technology running reliably.',
    tags: ['Helpdesk', 'Managed IT', 'Maintenance', 'SLA'],
  },
  {
    num: '08', Icon: Map,
    title: 'Digital Transformation Consulting',
    desc: 'Strategic technology roadmaps for businesses ready to modernise — from initial audit and gap analysis to phased implementation and long-term technology partnership.',
    tags: ['Strategy', 'Audit', 'Roadmap', 'Advisory'],
  },
];

export default function Services() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="services" style={{ padding: '100px 5%', background: 'var(--navy)' }}>

      {/* Header */}
      <div style={{ marginBottom: '3.5rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--electric)', marginBottom: '0.8rem' }}>
          What We Do
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.9rem,3.5vw,2.8rem)', fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.8px', lineHeight: 1.15, marginBottom: '1rem' }}>
          Full-stack technology services<br />for industrial operations
        </h2>
        <p style={{ color: 'var(--slate)', fontSize: '1.05rem', maxWidth: '540px', lineHeight: 1.7 }}>
          From network design to custom software, we build the digital backbone that keeps your operations running at every scale.
        </p>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1px',
        background: 'rgba(0,180,216,0.08)',
        border: '1px solid rgba(0,180,216,0.08)',
        borderRadius: '14px', overflow: 'hidden',
      }}>
        {services.map((s, i) => {
          const isHovered = hovered === i;
          return (
            <div key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                background: isHovered ? 'var(--card-bg)' : 'var(--navy)',
                padding: '2.2rem',
                transition: 'background 0.25s',
                cursor: 'default',
              }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', color: 'var(--electric)', opacity: 0.5, marginBottom: '1.2rem' }}>
                {s.num}
              </div>
              <div style={{ marginBottom: '1rem', color: isHovered ? 'var(--electric)' : 'var(--slate)', transition: 'color 0.25s' }}>
                <s.Icon size={28} />
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 600, color: 'var(--white)', marginBottom: '0.75rem' }}>
                {s.title}
              </div>
              <div style={{ fontSize: '0.88rem', color: 'var(--slate)', lineHeight: 1.65 }}>
                {s.desc}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '1.2rem' }}>
                {s.tags.map((t, j) => (
                  <span key={j} style={{
                    fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.4px',
                    background: 'rgba(0,180,216,0.08)',
                    color: 'var(--electric)',
                    border: '1px solid rgba(0,180,216,0.2)',
                    padding: '0.2rem 0.6rem', borderRadius: '4px',
                  }}>{t}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
